import { Controller } from 'react-hook-form';
import { InputAdornment, Stack, TextField } from '@mui/material';
import { useTranslation } from 'react-i18next';
import type { UseMutationResult } from '@tanstack/react-query';
import { FormDialog } from '../../../shared/components/FormDialog';
import { DatePickerPtBrField } from '../../../shared/components/DatePickerPtBr';
import { useCrudFormDialog } from '../../../shared/api/useCrudFormDialog';
import { employeeValeInputSchema, type EmployeeValeFormValues } from './schemas';
import type { EmployeeValeDto, EmployeeValeInput } from './types';

export interface EmployeeValeFormDialogProps {
  open: boolean;
  vale: EmployeeValeDto | null;
  hooks: {
    useCreate: () => UseMutationResult<EmployeeValeDto, Error, EmployeeValeInput>;
    useUpdate: () => UseMutationResult<
      EmployeeValeDto,
      Error,
      { id: number; input: EmployeeValeInput }
    >;
  };
  onClose: () => void;
}

const DEFAULT_VALUES: EmployeeValeFormValues = {
  dataSolicitacao: new Date().toISOString().slice(0, 10),
  valor: 0,
  nroParcelas: 1,
  vencimento1aParcela: '',
};

export function EmployeeValeFormDialog({
  open,
  vale,
  hooks,
  onClose,
}: EmployeeValeFormDialogProps): JSX.Element {
  const { t } = useTranslation();

  const { form, isEdit, onSubmit, serverError } = useCrudFormDialog<
    EmployeeValeFormValues,
    EmployeeValeDto,
    EmployeeValeInput,
    number
  >({
    open,
    entity: vale,
    schema: employeeValeInputSchema,
    defaultValues: DEFAULT_VALUES,
    fromEntity: (v) => ({
      dataSolicitacao: v.dataSolicitacao,
      valor: v.valor,
      nroParcelas: v.nroParcelas,
      vencimento1aParcela: v.vencimento1aParcela,
    }),
    toInput: (v) => ({
      dataSolicitacao: v.dataSolicitacao,
      valor: v.valor,
      nroParcelas: v.nroParcelas,
      vencimento1aParcela: v.vencimento1aParcela,
    }),
    getId: (v) => v.id,
    hooks: { useCreate: hooks.useCreate, useUpdate: hooks.useUpdate },
    messages: {
      created: t('features.colaboradores.vales.toasts.created'),
      updated: t('features.colaboradores.vales.toasts.updated'),
    },
    focusField: undefined,
    onClose,
  });

  return (
    <FormDialog
      open={open}
      title={
        isEdit
          ? t('features.colaboradores.vales.dialog.editTitle')
          : t('features.colaboradores.vales.dialog.newTitle')
      }
      form={form}
      onSubmit={onSubmit}
      onCancel={onClose}
      serverError={serverError}
      maxWidth="sm"
    >
      <Stack spacing={2}>
        <DatePickerPtBrField
          control={form.control}
          name="dataSolicitacao"
          label={t('features.colaboradores.vales.fields.dataSolicitacao')}
          slotProps={{
            textField: {
              fullWidth: true,
              helperText: t('features.colaboradores.vales.fields.dataSolicitacaoHelper'),
            },
          }}
        />

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
          <Controller
            control={form.control}
            name="valor"
            render={({ field, fieldState }) => (
              <TextField
                label={t('features.colaboradores.vales.fields.valor')}
                fullWidth
                type="number"
                value={Number.isFinite(field.value) ? field.value : ''}
                onChange={(e) => {
                  const v = e.target.value;
                  field.onChange(v === '' ? Number.NaN : Number.parseFloat(v));
                }}
                onBlur={field.onBlur}
                inputRef={field.ref}
                inputProps={{ step: '0.01', min: 0, inputMode: 'decimal' }}
                InputProps={{
                  startAdornment: <InputAdornment position="start">R$</InputAdornment>,
                }}
                error={Boolean(fieldState.error)}
                helperText={
                  fieldState.error?.message ?? t('features.colaboradores.vales.fields.valorHelper')
                }
              />
            )}
          />

          <Controller
            control={form.control}
            name="nroParcelas"
            render={({ field, fieldState }) => (
              <TextField
                label={t('features.colaboradores.vales.fields.nroParcelas')}
                fullWidth
                type="number"
                value={Number.isFinite(field.value) ? field.value : ''}
                onChange={(e) => {
                  const v = e.target.value;
                  field.onChange(v === '' ? Number.NaN : Number.parseInt(v, 10));
                }}
                onBlur={field.onBlur}
                inputRef={field.ref}
                inputProps={{ step: '1', min: 1, inputMode: 'numeric' }}
                error={Boolean(fieldState.error)}
                helperText={fieldState.error?.message ?? ' '}
                sx={{ maxWidth: { sm: 160 } }}
              />
            )}
          />
        </Stack>

        <DatePickerPtBrField
          control={form.control}
          name="vencimento1aParcela"
          label={t('features.colaboradores.vales.fields.vencimento1aParcela')}
          slotProps={{
            textField: {
              fullWidth: true,
              helperText: t('features.colaboradores.vales.fields.vencimento1aParcelaHelper'),
            },
          }}
        />
      </Stack>
    </FormDialog>
  );
}
